/**
 * `Viewport`: a canvas drawn by the wgpu renderer (WebAssembly, main thread).
 *
 * * Backends are tried in the given order (`['webgpu', 'webgl2']` by default);
 *   every attempt and its error is reported in `attempts` — nothing falls back
 *   silently, and there is no Canvas2D renderer.
 * * The canvas follows its container (ResizeObserver, exact device-pixel size)
 *   and the device-pixel ratio. DOM coordinates are CSS pixels everywhere.
 * * Frames are drawn on demand (scene change, camera change, overlay change).
 * * WebGPU device loss and WebGL context loss are recovered by creating a new
 *   renderer and requesting a full scene from the engine.
 */
import { DEFAULT_CAMERA, fitBounds, isValidCamera, panBy, screenToWorld, worldToScreen, zoomAt, } from './camera.js';
import { themeToWire } from './color.js';
import { DotloomError } from './protocol.js';
export const DEFAULT_GRID = { visible: true, spacing: 10, majorEvery: 10, minPx: 8 };
export const RENDER_PROTOCOL = 1;
/** Yield to the event loop for one task (MessageChannel: no timer clamping). */
function yieldTask() {
    return new Promise((resolve) => {
        const ch = new MessageChannel();
        ch.port1.onmessage = () => {
            ch.port1.close();
            resolve();
        };
        ch.port2.postMessage(null);
    });
}
let modulePromise = null;
/** Load (once) the renderer WebAssembly module. */
export function loadRenderer(url) {
    modulePromise ??= (async () => {
        const m = await import('./wasm/render/dotloom_render_web.js');
        await m.default(url === undefined ? undefined : { module_or_path: url });
        if (m.renderProtocol() !== RENDER_PROTOCOL) {
            throw new DotloomError({
                code: 'protocol',
                message: `renderer protocol ${m.renderProtocol()} != ${RENDER_PROTOCOL}`,
            });
        }
        return m;
    })();
    modulePromise.catch(() => {
        modulePromise = null;
    });
    return modulePromise;
}
function parseErr(e) {
    if (typeof e === 'string') {
        try {
            const v = JSON.parse(e);
            if (typeof v.code === 'string')
                return { code: v.code, message: v.message ?? '' };
        }
        catch {
            // plain message
        }
        return { code: 'render', message: e };
    }
    return { code: 'render', message: e instanceof Error ? e.message : String(e) };
}
const MAX_RECOVERIES = 3;
const RECOVERY_WINDOW_MS = 30_000;
const LOSS_RESTORE_TIMEOUT_MS = 1500;
/** A device lost this soon after creation marks its backend as unstable here. */
const EARLY_LOSS_MS = 5000;
const LOSS_WATCHDOG_MS = 500;
export class Viewport {
    container;
    canvas;
    renderer = null;
    mod;
    engine;
    backends;
    cam;
    sz = { width: 1, height: 1, dpr: 1 };
    devicePx = [1, 1];
    gridSettings;
    themeWire;
    overlayJson = '{}';
    hover = null;
    raf = null;
    dirty = true;
    disposed = false;
    lostState = null;
    recoveries = [];
    /** When the current renderer was created / the last backend change or loss. */
    createdAt = 0;
    lastChange = 0;
    /** Backends whose devices were lost right after creation (tried last). */
    unstable = new Set();
    listeners = new Map();
    cleanup = [];
    label;
    msaa;
    info = null;
    attempts = [];
    lastStats = null;
    constructor(container, engine, mod, opts) {
        this.container = container;
        this.engine = engine;
        this.mod = mod;
        this.backends = opts.backends ?? ['webgpu', 'webgl2'];
        this.cam = opts.camera && isValidCamera(opts.camera) ? opts.camera : DEFAULT_CAMERA;
        this.gridSettings = { ...DEFAULT_GRID, ...opts.grid };
        this.label = opts.label ?? 'Drawing canvas';
        this.msaa = opts.msaa ?? true;
        this.themeWire = this.resolveTheme(opts.theme);
        this.canvas = this.makeCanvas();
    }
    /** Create a viewport inside `container` (which must have a size). */
    static async create(container, engine, opts = {}) {
        const mod = await loadRenderer(opts.renderWasmUrl);
        const v = new Viewport(container, engine, mod, opts);
        v.measure();
        await v.start(v.backends);
        v.observe();
        return v;
    }
    resolveTheme(t) {
        const preset = this.mod.themePreset(t?.preset ?? 'light');
        if (!preset)
            throw new DotloomError({ code: 'invalid', message: `unknown theme preset ${t?.preset}` });
        return themeToWire(JSON.parse(preset), t?.colors);
    }
    makeCanvas() {
        const c = document.createElement('canvas');
        c.style.display = 'block';
        c.style.width = '100%';
        c.style.height = '100%';
        c.style.touchAction = 'none';
        c.style.outline = 'none';
        c.setAttribute('role', 'img');
        c.setAttribute('aria-label', this.label);
        c.dataset.dotloom = 'canvas';
        this.container.appendChild(c);
        return c;
    }
    replaceCanvas() {
        const old = this.canvas;
        this.canvas = this.makeCanvas();
        old.remove();
        this.canvas.width = this.devicePx[0];
        this.canvas.height = this.devicePx[1];
        this.attachCanvasListeners();
    }
    on(name, cb) {
        let set = this.listeners.get(name);
        if (!set) {
            set = new Set();
            this.listeners.set(name, set);
        }
        set.add(cb);
        return () => set?.delete(cb);
    }
    emit(name, v) {
        for (const cb of [...(this.listeners.get(name) ?? [])])
            cb(v);
    }
    measure() {
        const r = this.container.getBoundingClientRect();
        const dpr = globalThis.devicePixelRatio || 1;
        this.setSize(r.width, r.height, Math.round(r.width * dpr), Math.round(r.height * dpr));
    }
    setSize(cssW, cssH, devW, devH) {
        const w = Math.max(1, cssW);
        const h = Math.max(1, cssH);
        const dw = Math.max(1, Math.min(16384, devW));
        const dh = Math.max(1, Math.min(16384, devH));
        // The ratio actually used by the canvas, not window.devicePixelRatio (zoom,
        // fractional scaling and rounding all end up in this one number).
        this.sz = { width: w, height: h, dpr: dw / w };
        this.devicePx = [dw, dh];
        this.canvas.width = dw;
        this.canvas.height = dh;
        try {
            this.renderer?.resize(dw, dh);
        }
        catch (e) {
            this.emit('error', parseErr(e));
        }
        this.requestRender();
    }
    observe() {
        if (typeof ResizeObserver === 'function') {
            const ro = new ResizeObserver((entries) => {
                const e = entries[0];
                if (!e)
                    return;
                const cssW = e.contentRect.width;
                const cssH = e.contentRect.height;
                const dev = e.devicePixelContentBoxSize?.[0];
                const dpr = globalThis.devicePixelRatio || 1;
                const est = [Math.round(cssW * dpr), Math.round(cssH * dpr)];
                // The exact device-pixel box is preferred, but some environments (e.g.
                // emulated device scale factors) report CSS pixels there: only trust it
                // when it agrees with devicePixelRatio to within a pixel.
                const exact = dev && Math.abs(dev.inlineSize - est[0]) <= 1 && Math.abs(dev.blockSize - est[1]) <= 1 ? dev : null;
                this.setSize(cssW, cssH, exact ? exact.inlineSize : est[0], exact ? exact.blockSize : est[1]);
            });
            try {
                ro.observe(this.container, { box: 'device-pixel-content-box' });
            }
            catch {
                ro.observe(this.container);
            }
            this.cleanup.push(() => ro.disconnect());
        }
        // DPR changes (moving between monitors, browser zoom) without a size change.
        const watchDpr = () => {
            if (typeof matchMedia !== 'function')
                return;
            const mq = matchMedia(`(resolution: ${globalThis.devicePixelRatio || 1}dppx)`);
            const onChange = () => {
                this.measure();
                watchDpr();
            };
            mq.addEventListener('change', onChange, { once: true });
            this.cleanup.push(() => mq.removeEventListener('change', onChange));
        };
        watchDpr();
        // Device loss is reported asynchronously; notice it even when no frame is drawn.
        const watchdog = setInterval(() => {
            const r = this.renderer;
            if (!r || this.lostState || this.disposed)
                return;
            const lost = this.lostReason(r);
            if (lost) {
                this.markLost(this.info?.backend ?? 'webgpu', lost);
                void this.recover();
            }
        }, LOSS_WATCHDOG_MS);
        this.cleanup.push(() => clearInterval(watchdog));
        this.cleanup.push(this.engine.on('scene', (s) => {
            if (!this.renderer || this.lostState)
                return;
            try {
                this.renderer.applyDelta(new Uint8Array(s.delta));
                this.requestRender();
            }
            catch (e) {
                this.emit('error', parseErr(e));
            }
        }));
    }
    canvasListeners = [];
    attachCanvasListeners() {
        for (const f of this.canvasListeners.splice(0))
            f();
        const lost = (ev) => {
            ev.preventDefault();
            this.markLost('webgl2', 'WebGL context lost');
        };
        const restored = () => {
            void this.recover();
        };
        this.canvas.addEventListener('webglcontextlost', lost);
        this.canvas.addEventListener('webglcontextrestored', restored);
        this.canvasListeners.push(() => this.canvas.removeEventListener('webglcontextlost', lost), () => this.canvas.removeEventListener('webglcontextrestored', restored));
    }
    async start(order) {
        const attempts = [];
        for (const backend of order) {
            if (attempts.length > 0)
                this.replaceCanvas();
            else
                this.attachCanvasListeners();
            try {
                const r = await this.mod.WebRenderer.create(this.canvas, backend, this.devicePx[0], this.devicePx[1], JSON.stringify({ msaa: this.msaa }));
                attempts.push({ backend, ok: true });
                this.renderer = r;
                this.createdAt = performance.now();
                this.lastChange = this.createdAt;
                this.info = JSON.parse(r.info());
                this.attempts = attempts;
                this.applySettings();
                this.lostState = null;
                this.emit('backend', { info: this.info, attempts });
                await this.engine.requestFullScene();
                this.requestRender();
                return;
            }
            catch (e) {
                attempts.push({ backend, ok: false, error: parseErr(e).message });
            }
        }
        this.attempts = attempts;
        throw new DotloomError({
            code: 'init',
            message: `no renderer backend available (${attempts.map((a) => `${a.backend}: ${a.error}`).join('; ')})`,
            details: { attempts },
        });
    }
    applySettings() {
        const r = this.renderer;
        if (!r)
            return;
        r.setTheme(JSON.stringify(this.themeWire));
        r.setGrid(JSON.stringify(this.gridSettings));
        r.setOverlay(this.overlayJson);
        r.setHover(this.hover ?? -1);
    }
    markLost(backend, reason) {
        if (this.lostState)
            return;
        this.lostState = reason;
        this.lastChange = performance.now();
        // Lost right after creation (e.g. software adapters that cannot keep a device):
        // recover on the other backends first.
        if (this.lastChange - this.createdAt < EARLY_LOSS_MS)
            this.unstable.add(backend);
        this.emit('lost', { backend, reason });
        // If the browser never restores the context, recover on a fresh canvas.
        setTimeout(() => {
            if (this.lostState === reason)
                void this.recover();
        }, LOSS_RESTORE_TIMEOUT_MS);
    }
    recovering = false;
    async recover() {
        if (this.disposed || this.recovering || !this.lostState)
            return;
        this.recovering = true;
        try {
            await this.recoverNow();
        }
        finally {
            this.recovering = false;
        }
    }
    async recoverNow() {
        const now = Date.now();
        this.recoveries = this.recoveries.filter((t) => now - t < RECOVERY_WINDOW_MS);
        if (this.recoveries.length >= MAX_RECOVERIES) {
            this.emit('error', { code: 'lost', message: 'the GPU device was lost repeatedly; rendering stopped' });
            return;
        }
        this.recoveries.push(now);
        const backend = this.info?.backend ?? this.backends[0] ?? 'webgpu';
        try {
            this.renderer?.dispose();
        }
        catch {
            // already unusable
        }
        this.renderer = null;
        this.replaceCanvas();
        try {
            const rest = this.backends.filter((b) => b !== backend);
            const order = this.unstable.has(backend) ? [...rest, backend] : [backend, ...rest];
            await this.start(order);
            this.emit('restored', { backend: this.info?.backend ?? backend, recoveries: this.recoveries.length });
        }
        catch (e) {
            this.emit('error', parseErr(e));
        }
    }
    lostReason(r) {
        try {
            return r.lost() ?? null;
        }
        catch {
            return 'renderer unavailable';
        }
    }
    /** Simulate GPU device/context loss (tests). */
    simulateContextLoss() {
        if (!this.renderer)
            return;
        if (this.info?.backend === 'webgl2') {
            // Get the extension before losing the context: a lost context returns null.
            const ext = this.canvas.getContext('webgl2')?.getExtension('WEBGL_lose_context');
            ext?.loseContext();
            // Restore shortly after, as a real driver reset would.
            setTimeout(() => ext?.restoreContext(), 50);
        }
        else {
            this.renderer.loseDeviceForTesting();
            this.requestRender();
        }
    }
    // --- camera ------------------------------------------------------------
    get size() {
        return this.sz;
    }
    get camera() {
        return this.cam;
    }
    get grid() {
        return this.gridSettings;
    }
    get backend() {
        return this.info?.backend ?? null;
    }
    setCamera(c) {
        if (!isValidCamera(c))
            return;
        this.cam = c;
        this.emit('camera', c);
        this.requestRender();
    }
    panBy(dx, dy) {
        this.setCamera(panBy(this.cam, dx, dy));
    }
    zoomAt(x, y, factor) {
        this.setCamera(zoomAt(this.cam, this.sz, x, y, factor));
    }
    screenToWorld(x, y) {
        return screenToWorld(this.cam, this.sz, x, y);
    }
    worldToScreen(p) {
        return worldToScreen(this.cam, this.sz, p);
    }
    /** Scene bounds known to the renderer, or `null` when empty. */
    sceneBounds() {
        if (!this.renderer)
            return null;
        return JSON.parse(this.renderer.sceneBounds());
    }
    /** Fit `bounds` (default: the whole scene). */
    async fit(bounds, margin = 32) {
        const b = bounds ?? this.sceneBounds();
        if (b)
            this.setCamera(fitBounds(this.sz, b, margin));
    }
    // --- appearance --------------------------------------------------------
    setTheme(t) {
        this.themeWire = this.resolveTheme(t);
        this.renderer?.setTheme(JSON.stringify(this.themeWire));
        this.requestRender();
    }
    setGrid(g) {
        this.gridSettings = { ...this.gridSettings, ...g };
        this.renderer?.setGrid(JSON.stringify(this.gridSettings));
        this.requestRender();
    }
    setOverlay(o) {
        const json = JSON.stringify({
            markers: o.markers ?? [],
            marquee: o.marquee ?? null,
            crossing: o.crossing ?? false,
            guides: o.guides ?? [],
            sketch: o.sketch ?? [],
        });
        if (json === this.overlayJson)
            return;
        this.overlayJson = json;
        try {
            this.renderer?.setOverlay(json);
        }
        catch (e) {
            this.emit('error', parseErr(e));
        }
        this.requestRender();
    }
    setHover(id) {
        if (id === this.hover)
            return;
        this.hover = id;
        this.renderer?.setHover(id ?? -1);
        this.requestRender();
    }
    // --- frames ------------------------------------------------------------
    requestRender() {
        this.dirty = true;
        if (this.raf !== null || this.disposed)
            return;
        if (typeof requestAnimationFrame !== 'function')
            return;
        this.raf = requestAnimationFrame(() => {
            this.raf = null;
            this.frame();
        });
    }
    /** Draw now (normally driven by `requestRender`). */
    frame() {
        if (!this.dirty || !this.renderer || this.lostState || this.disposed)
            return null;
        this.dirty = false;
        const r = this.renderer;
        try {
            r.setView(this.cam.center[0], this.cam.center[1], this.cam.scale, this.sz.width, this.sz.height, this.sz.dpr);
            const t0 = performance.now();
            const json = r.render();
            const cpuMs = performance.now() - t0;
            if (json === 'null') {
                // Surface reconfigured or occluded; try again next frame.
                this.requestRender();
                return null;
            }
            const stats = { ...JSON.parse(json), cpuMs };
            this.lastStats = stats;
            this.emit('frame', stats);
            return stats;
        }
        catch (e) {
            const err = parseErr(e);
            const lost = err.code === 'lost' ? err.message : this.lostReason(r);
            if (lost) {
                this.markLost(this.info?.backend ?? 'webgpu', lost);
                void this.recover();
            }
            else {
                this.emit('error', err);
                // The device-lost notification can arrive after the frame that failed.
                setTimeout(() => {
                    const late = r === this.renderer ? this.lostReason(r) : null;
                    if (late) {
                        this.markLost(this.info?.backend ?? 'webgpu', late);
                        void this.recover();
                    }
                }, 100);
            }
            return null;
        }
    }
    /**
     * Resolve once rendering has been running on the same backend, without a device
     * loss, for `quietMs` — e.g. before taking a screenshot or thumbnail right after
     * start-up, when a backend that loses its device immediately is replaced.
     * Rejects after `timeoutMs`.
     */
    async whenStable(quietMs = 500, timeoutMs = 15_000) {
        const t0 = performance.now();
        for (;;) {
            if (this.disposed)
                throw new DotloomError({ code: 'disposed', message: 'viewport disposed' });
            const now = performance.now();
            if (this.renderer && !this.lostState && now - this.lastChange >= quietMs)
                return;
            if (now - t0 > timeoutMs) {
                throw new DotloomError({ code: 'render', message: 'rendering did not become stable' });
            }
            await new Promise((r) => setTimeout(r, 50));
        }
    }
    /**
     * Renderer memory: WebAssembly linear memory (high-water mark, shared by all
     * viewports of the page) and GPU buffers/textures of this viewport (last frame).
     */
    memoryStats() {
        return { wasmBytes: this.mod.memoryBytes(), gpuBytes: this.lastStats?.gpuBytes ?? 0 };
    }
    /**
     * Resolve once the GPU has finished every frame submitted so far (frame-time
     * measurements: input → GPU done). Rendering never needs it. Resolves early if
     * the renderer is lost, replaced or disposed, or after `timeoutMs`.
     */
    async gpuIdle(timeoutMs = 2000) {
        const r = this.renderer;
        if (!r || this.lostState || this.disposed)
            return;
        r.markWork();
        const t0 = performance.now();
        while (!r.workDone()) {
            if (r !== this.renderer || this.lostState || this.disposed || performance.now() - t0 > timeoutMs)
                return;
            await yieldTask();
        }
    }
    /**
     * Export the current view as PNG (device-pixel resolution). The frame is
     * rendered and read back in the same task, so it does not depend on the
     * compositor. Hide the grid with `grid: false`.
     */
    async exportPng(opts = {}) {
        const r = this.renderer;
        if (!r || this.lostState)
            throw new DotloomError({ code: 'render', message: 'renderer not available' });
        const grid = this.gridSettings;
        if (opts.grid === false)
            r.setGrid(JSON.stringify({ ...grid, visible: false }));
        const overlay = this.overlayJson;
        r.setOverlay('{}');
        try {
            this.dirty = true;
            this.frame();
            const blob = await new Promise((resolve) => this.canvas.toBlob(resolve, 'image/png'));
            if (!blob)
                throw new DotloomError({ code: 'render', message: 'canvas readback failed' });
            return blob;
        }
        finally {
            r.setGrid(JSON.stringify(grid));
            r.setOverlay(overlay);
            this.requestRender();
        }
    }
    /** Remove the canvas and release GPU resources and listeners. */
    dispose() {
        if (this.disposed)
            return;
        this.disposed = true;
        if (this.raf !== null && typeof cancelAnimationFrame === 'function')
            cancelAnimationFrame(this.raf);
        for (const f of this.cleanup.splice(0))
            f();
        for (const f of this.canvasListeners.splice(0))
            f();
        try {
            this.renderer?.dispose();
            this.renderer?.free();
        }
        catch {
            // already released
        }
        this.renderer = null;
        this.canvas.remove();
        this.listeners.clear();
    }
    get isDisposed() {
        return this.disposed;
    }
}
//# sourceMappingURL=viewport.js.map